import React from "react";
import {
  FiActivity,
  FiBarChart2,
  FiCalendar,
  FiCheckCircle,
  FiCreditCard,
  FiGlobe,
  FiImage,
  FiLayers,
  FiMonitor,
  FiShield,
  FiSmartphone,
  FiTrendingUp,
  FiUsers,
  FiZap,
} from "react-icons/fi";
import "./CommercialSite.css";

const heroStats = [
  { value: "All in one", label: "PMS, RMS y motor de reservas" },
  { value: "Diario", label: "Precios de competencia actualizados" },
  { value: "Directo", label: "Venta online con Stripe integrado" },
];

const trustPills = [
  { label: "Reservas y front desk", Icon: FiMonitor },
  { label: "Revenue management integrado", Icon: FiTrendingUp },
  { label: "Motor de reservas propio", Icon: FiCreditCard },
  { label: "Channel managers conectados", Icon: FiGlobe },
  { label: "Housekeeping y operacion diaria", Icon: FiCheckCircle },
  { label: "Usuarios y permisos", Icon: FiShield },
];

const solutionCards = [
  {
    title: "Operacion diaria clara",
    description:
      "Recepcion, direccion y equipo operativo trabajan con una vista comun de reservas, llegadas, salidas, ocupacion y actividad diaria.",
    Icon: FiBarChart2,
    tags: ["PMS", "Front desk", "Operacion"],
  },
  {
    title: "Revenue management conectado a la realidad del hotel",
    description:
      "Pilcore combina ocupacion, pick-up, precios de mercado y lectura comercial para apoyar decisiones de tarifa con contexto real.",
    Icon: FiTrendingUp,
    tags: ["RMS", "Competencia", "IA"],
  },
  {
    title: "Menos friccion entre departamentos",
    description:
      "Reservas, pricing, venta directa, housekeeping y coordinacion interna trabajan sobre la misma informacion.",
    Icon: FiUsers,
    tags: ["All in one", "Coordinacion", "Control"],
  },
];

const modules = [
  {
    title: "PMS y Front Desk",
    description:
      "Gestion completa de reservas, rooming, check-in, check-out y operativa diaria desde una sola interfaz.",
    Icon: FiMonitor,
  },
  {
    title: "RMS y pricing",
    description:
      "Lectura de ocupacion, competidores, evolucion comercial y recomendacion de tarifas con apoyo de IA.",
    Icon: FiTrendingUp,
  },
  {
    title: "Motor de reservas",
    description:
      "Venta directa conectada al PMS y a la estrategia de precios del hotel, con pago online integrado mediante Stripe.",
    Icon: FiCreditCard,
  },
  {
    title: "Distribucion y conectividad",
    description:
      "Sincronizacion con channel managers para mantener tarifas, restricciones y disponibilidad alineadas.",
    Icon: FiGlobe,
  },
  {
    title: "Housekeeping y coordinacion",
    description:
      "Herramientas operativas para el dia a dia del hotel, seguimiento interno y coordinacion entre equipos.",
    Icon: FiCalendar,
  },
  {
    title: "Compras e inventario",
    description:
      "Modulo propio para requisiciones, proveedores e inventario, integrado dentro del ecosistema Pilcore.",
    Icon: FiLayers,
  },
];

const galleryItems = [
  {
    title: "Dashboard operativo y comercial",
    description:
      "Vista general con KPIs, evolucion de ocupacion, ventas y lectura inmediata del negocio.",
    image: "/commercial/pms-dashboard.png",
    imageAlt: "Pilcore dashboard operativo y comercial",
    Icon: FiActivity,
    variant: "wide",
  },
  {
    title: "Disponibilidad por categoria",
    description:
      "Control visual de inventario, disponibilidad y ocupacion por categoria y fecha.",
    image: "/commercial/availability-summary.png",
    imageAlt: "Pilcore availability summary",
    Icon: FiCheckCircle,
    variant: "regular",
  },
  {
    title: "Asignacion visual de habitaciones",
    description:
      "Gestion de habitaciones y reservas mediante una vista operativa pensada para recepcion.",
    image: "/commercial/occupancy-grid.png",
    imageAlt: "Pilcore occupancy grid",
    Icon: FiMonitor,
    variant: "regular",
  },
  {
    title: "Control de tarifas y restricciones",
    description:
      "Matriz para gestionar precios, estancias minimas, maximas y disponibilidad comercial.",
    image: "/commercial/room-rates-matrix.png",
    imageAlt: "Pilcore room rates matrix",
    Icon: FiTrendingUp,
    variant: "wide",
  },
];

const whyPilcore = [
  {
    title: "Menos herramientas separadas",
    description:
      "Pilcore reduce la fragmentacion entre PMS, pricing, distribucion y venta directa.",
  },
  {
    title: "Decisiones mas rapidas",
    description:
      "La informacion importante esta visible en una sola plataforma y con lectura operativa clara.",
  },
  {
    title: "Mas control sobre venta y rentabilidad",
    description:
      "Disponibilidad, tarifas y venta directa trabajan juntas para mejorar el control comercial del hotel.",
  },
];

function CommercialSite() {
  return (
    <div className="commercial-shell">
      <header className="commercial-topbar">
        <a className="commercial-brand" href="#inicio">
          <div className="commercial-brandMark">
            <img src="/pilcore_new_no_bg_no_letters.png" alt="Pilcore" />
          </div>
          <div>
            <div className="commercial-brandEyebrow">Pilcore</div>
            <div className="commercial-brandName">PMS, RMS y motor de reservas para hoteles</div>
          </div>
        </a>

        <nav className="commercial-nav" aria-label="Secciones comerciales">
          <a href="#producto">Producto</a>
          <a href="#soluciones">Soluciones</a>
          <a href="#modulos">Modulos</a>
          <a href="#ver-pilcore">Ver Pilcore</a>
          <a href="#contacto">Solicitar demo</a>
        </nav>
      </header>

      <main className="commercial-main">
        <section id="inicio" className="commercial-hero">
          <div className="commercial-heroCopy">
            <span className="commercial-kicker">Software hotelero todo en uno</span>

            <h1>Controla reservas, tarifas y operacion diaria desde una sola plataforma</h1>

            <p className="commercial-lead">
              Pilcore unifica PMS, revenue management, motor de reservas, pagos online,
              channel managers y operacion interna del hotel en un unico sistema diseñado para
              trabajar con datos reales y tomar decisiones rapidas.
            </p>

            <div className="commercial-actions">
              <a className="commercial-btn commercial-btn--primary" href="#contacto">
                Solicitar demo privada
              </a>
              <a className="commercial-btn commercial-btn--ghost" href="#ver-pilcore">
                Ver Pilcore en funcionamiento
              </a>
            </div>

            <div className="commercial-stats" aria-label="Resumen del producto">
              {heroStats.map((item) => (
                <article key={item.label} className="commercial-statCard">
                  <div className="commercial-statValue">{item.value}</div>
                  <div className="commercial-statLabel">{item.label}</div>
                </article>
              ))}
            </div>
          </div>

          <div className="commercial-heroVisual">
            <article className="commercial-spotlightCard commercial-spotlightCard--image">
              <div className="commercial-spotlightBadge">Vista principal del sistema</div>

              <div className="commercial-spotlightImageWrap">
                <img
                  className="commercial-spotlightImage"
                  src="/commercial/pms-dashboard.png"
                  alt="Pilcore PMS dashboard"
                />
              </div>

              <div className="commercial-spotlightText">
                <span>Operacion, revenue y reservas</span>
                <strong>Una interfaz pensada para entender la situacion del hotel en segundos.</strong>
              </div>
            </article>

            <article className="commercial-miniPanel">
              <span className="commercial-miniLabel">Plataforma</span>
              <strong>PMS · RMS · Motor de reservas</strong>
              <p>
                Reservas, precios, disponibilidad, venta directa y operacion diaria conectadas en
                una sola herramienta.
              </p>
            </article>

            <article className="commercial-miniPanel commercial-miniPanel--accent">
              <span className="commercial-miniLabel">Propuesta de valor</span>
              <strong>Menos herramientas. Mas control.</strong>
              <p>
                Pilcore reduce la fragmentacion entre sistemas y ayuda al hotel a trabajar con una
                sola fuente de verdad.
              </p>
            </article>

            <a
              className="commercial-imagePlaceholder commercial-imagePlaceholder--hero commercial-linkCard"
              href="#ver-pilcore"
            >
              <FiImage size={22} />
              <div>
                <strong>Ver capturas del producto</strong>
                <span>Dashboard, disponibilidad, habitaciones y tarifas</span>
              </div>
            </a>
          </div>
        </section>

        <section className="commercial-trustBand" aria-label="Capacidades principales">
          {trustPills.map(({ label, Icon }) => (
            <div key={label} className="commercial-trustPill">
              <Icon size={15} />
              <span>{label}</span>
            </div>
          ))}
        </section>

        <section id="producto" className="commercial-section commercial-section--split">
          <div className="commercial-sectionHeading commercial-sectionHeading--compact">
            <span className="commercial-kicker">Producto</span>
            <h2>Una sola plataforma para reservas, precios y operacion</h2>
            <p>
              Pilcore esta diseñado para hoteles que necesitan trabajar con rapidez, claridad y
              control real. Reune en un unico sistema la gestion de reservas, la estrategia de
              precios, la venta directa y la operativa diaria del establecimiento.
            </p>
          </div>

          <div className="commercial-sectionVisual">
            <img
              className="commercial-sectionImage"
              src="/commercial/availability-summary.png"
              alt="Pilcore availability summary"
            />
          </div>
        </section>

        <section id="soluciones" className="commercial-section">
          <div className="commercial-sectionHeading">
            <span className="commercial-kicker">Soluciones</span>
            <h2>Una plataforma diseñada para la operacion real del hotel</h2>
            <p>
              Pilcore conecta la lectura operativa y la lectura comercial para que recepcion,
              revenue y direccion trabajen con informacion consistente y accionable.
            </p>
          </div>

          <div className="commercial-featureGrid">
            {solutionCards.map(({ title, description, Icon, tags }) => (
              <article key={title} className="commercial-featureCard">
                <div className="commercial-featureIcon">
                  <Icon size={18} />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
                <div className="commercial-tagRow">
                  {tags.map((tag) => (
                    <span key={tag} className="commercial-tag">
                      {tag}
                    </span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section id="modulos" className="commercial-section">
          <div className="commercial-sectionHeading">
            <span className="commercial-kicker">Modulos</span>
            <h2>Modulos que trabajan juntos</h2>
            <p>
              Pilcore no depende de piezas desconectadas. Sus modulos comparten informacion para
              que el hotel gestione mejor su operacion, sus tarifas y sus ventas.
            </p>
          </div>

          <div className="commercial-moduleGrid commercial-moduleGrid--3">
            {modules.map(({ title, description, Icon }) => (
              <article key={title} className="commercial-moduleCard">
                <div className="commercial-moduleIcon">
                  <Icon size={18} />
                </div>
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="ver-pilcore" className="commercial-section commercial-section--media">
          <div className="commercial-sectionHeading commercial-sectionHeading--compact">
            <span className="commercial-kicker">Ver Pilcore</span>
            <h2>Pantallas reales del producto</h2>
            <p>
              Una seleccion de vistas reales del sistema para mostrar como se gestiona la
              operacion diaria, la disponibilidad, la asignacion de habitaciones y el control de
              tarifas.
            </p>

            <div className="commercial-inlineActions">
              <a className="commercial-btn commercial-btn--ghost" href="#contacto">
                Solicitar demo guiada
              </a>
            </div>
          </div>

          <div className="commercial-mediaGrid">
            {galleryItems.map(({ title, description, image, imageAlt, Icon, variant }) => (
              <article
                key={title}
                className={`commercial-mediaCard commercial-mediaCard--${variant}`}
              >
                <div className="commercial-mediaAssetWrap">
                  <img className="commercial-mediaAsset" src={image} alt={imageAlt} />
                </div>

                <div className="commercial-mediaInner commercial-mediaInner--overlay">
                  <Icon size={20} />
                  <strong>{title}</strong>
                  <p>{description}</p>
                  <span className="commercial-mediaHint">Pantalla real de Pilcore</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="commercial-section">
          <div className="commercial-sectionHeading">
            <span className="commercial-kicker">Por que Pilcore</span>
            <h2>Una plataforma unificada aporta claridad operativa y control comercial</h2>
          </div>

          <div className="commercial-featureGrid">
            {whyPilcore.map(({ title, description }) => (
              <article key={title} className="commercial-featureCard">
                <h3>{title}</h3>
                <p>{description}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="contacto" className="commercial-section">
          <div className="commercial-ctaPanel">
            <div>
              <span className="commercial-kicker">Solicitar demo</span>
              <h2>Descubre Pilcore en funcionamiento</h2>
              <p>
                Solicita una demo privada y te mostraremos como Pilcore gestiona reservas, tarifas,
                disponibilidad y operacion diaria desde una sola plataforma, con un recorrido guiado
                sobre casos reales de uso hotelero.
              </p>
            </div>

            <div className="commercial-ctaBox">
              <div className="commercial-ctaRow">
                <span>Formato</span>
                <strong>Demo privada guiada</strong>
              </div>
              <div className="commercial-ctaRow">
                <span>Canal</span>
                <strong>Formulario, Calendly o WhatsApp</strong>
              </div>
              <div className="commercial-ctaActions">
                <a className="commercial-btn commercial-btn--primary" href="mailto:demo@pilcore.com">
                  Solicitar demo
                </a>
                <a className="commercial-btn commercial-btn--ghost" href="#inicio">
                  Volver al inicio
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}

export default CommercialSite;